import { ArrowRightIcon } from '@heroicons/react/outline';
import React, { useEffect, useState, VFC } from 'react';
import { TextInput } from 'src/common/form/text-input';
import { IconButton } from 'src/common/icon-button';
import { Content } from 'src/common/interfaces/content.interface';
import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { MultiSelectItem, MultiSelectWithSearch } from 'src/common/multi-select/with-search';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { CheckmarkRegularIcon } from 'src/monet/icons';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentTags } from 'src/store/tags';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import {
  apiGetAllOrganizationTags,
  apiAddTagToContent,
  apiRemoveTagFromContent,
  apiCreateOrganizationTag,
  apiGetContentTags,
} from 'src/utils/journeyApi';

interface LibraryContentTagsPanelProps
  extends Pick<
    JourneyMenuPropsInterface,
    'openRef' | 'closeRef' | 'placement' | 'offset' | 'renderOrigin' | 'onOpen' | 'onClose'
  > {
  onBack: () => void;
  onTagsUpdate: (content: Content) => void;
  contentId: number;
}

export const LibraryContentTagsPanel: VFC<LibraryContentTagsPanelProps> = ({
  contentId,
  openRef,
  closeRef,
  placement,
  onOpen,
  onClose,
  onTagsUpdate,
  renderOrigin,
  onBack,
}) => {
  const { currentOrganization } = useCurrentOrganization();
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const [fullTagsList, setFullTagsList] = useState<MultiSelectItem[]>([]);
  const [listedTags, setListedTags] = useState<MultiSelectItem[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<MultiSelectItem[]>([]);
  const [newTagInput, setNewTagInput] = useState<string | null>(null);
  const { setCurrentTags } = useCurrentTags();
  const { checkFeature, showUpgradeModal } = useFeatureRestrictions();

  const fetchTags = async () => {
    const tagsResponse = await apiGetAllOrganizationTags(currentOrganization.id);
    const formattedTags = tagsResponse?.tags?.map((item: { id: number; name: string }) => {
      return { value: String(item.id), label: item.name };
    });
    setCurrentTags(tagsResponse.tags);
    setFullTagsList(formattedTags);
    setListedTags(formattedTags);
  };

  const fetchContentTags = async () => {
    const contentResponse = await apiGetContentTags(contentId);
    const contentTags = contentResponse.tags.map((tag: { id: number; name: string }) => ({
      value: String(tag.id),
      label: tag.name,
    }));
    setSelectedOptions(contentTags);
  };

  useEffect(() => {
    fetchTags();
    fetchContentTags();
  }, [currentOrganization.id, contentId]);

  const onSearchInputChanged = (searchInput: string) => {
    if (!searchInput) {
      setListedTags([...fullTagsList]);
      return;
    }
    setListedTags(fullTagsList.filter((tag) => tag.label.toLowerCase().includes(searchInput.toLowerCase())));
  };

  const addContentTag = async (tagId: string) => {
    await apiAddTagToContent(contentId, tagId).then((content: Content) => {
      const newTag = listedTags.find((option) => option.value === tagId);
      if (newTag) {
        setSelectedOptions([...selectedOptions, newTag]);
      }
      onTagsUpdate(content);
    });
  };

  const removeContentTag = async (tagId: string) => {
    await apiRemoveTagFromContent(contentId, tagId).then((content: Content) => {
      setSelectedOptions(selectedOptions.filter((option) => option.value !== tagId));
      onTagsUpdate(content);
    });
  };

  const onItemSelected = (selectedItem: MultiSelectItem) => {
    if (selectedOptions.find((selected) => selected.value === selectedItem.value)) {
      removeContentTag(selectedItem.value);
    } else {
      addContentTag(selectedItem.value);
    }
  };

  const saveNewTag = async () => {
    if (newTagInput?.length && newTagInput?.length > 0 && currentOrganization?.id) {
      // Check if user can create tags (Grow plan or higher)
      if (!checkFeature('tags_and_filters')) {
        showUpgradeModal('tags_and_filters', 'Upgrade to Grow or Scale plan to create custom tags');
        return;
      }
      
      await apiCreateOrganizationTag(currentOrganization.id, newTagInput)
        .then(({ tag }: { tag: { id: number; name: string } }) => {
          fetchTags();
          addContentTag(`${tag.id}`).then(() => {
            fetchContentTags();
          });
          setSuccessNotification('Tag created successfully');
        })
        .catch(() => {
          setErrorNotification('Something went wrong creating tag');
        });
    }
    setNewTagInput('');
  };

  return (
    <MultiSelectWithSearch
      name='content-tags'
      searchPlaceholder='Search for tags'
      items={listedTags}
      placement={placement}
      selectedItems={[]}
      className='pb-0'
      openRef={openRef}
      onOpen={onOpen}
      onClose={onClose}
      onBack={onBack}
      hasBackButton
      closeRef={closeRef}
      hideActionOnNotFound
      renderOrigin={renderOrigin}
      renderNotFound={() => <span>No results found</span>}
      onSearchInputChange={onSearchInputChanged}
      onItemSelect={onItemSelected}
      renderListItem={(item, onClick) => {
        const isItemSelected = selectedOptions.find((option) => option.value === item.value);
        return (
          <div className='flex items-center cursor-pointer hover:opacity-80' onClick={onClick}>
            {isItemSelected && <CheckmarkRegularIcon className='shrink-0' />}
            <div className={isItemSelected ? 'ml-2' : 'ml-7'}>{item.label}</div>
          </div>
        );
      }}
      renderFooter={() => (
        <>
          {checkFeature('tags_and_filters') ? (
            <TextInput
              id={`new-tag`}
              onChange={(e: any) => {
                e.preventDefault();
                setNewTagInput(e.target.value);
              }}
              value={newTagInput || ''}
              hideOptional
              trailingAddon={
                <IconButton onClick={saveNewTag}>
                  <ArrowRightIcon className='w-5 h-5' />
                </IconButton>
              }
              inputProps={{
                type: 'text',
                placeholder: 'New Tag',
              }}
            />
          ) : (
            <div className="p-3 text-center text-sm text-gray-500">
              <p>Tag creation requires Grow plan or higher</p>
              <button 
                onClick={() => showUpgradeModal('tags_and_filters', 'Upgrade to Grow or Scale plan to create custom tags')}
                className="text-blue-600 hover:text-blue-800 underline mt-1"
              >
                Upgrade now
              </button>
            </div>
          )}
        </>
      )}
    />
  );
};
